/** Minimum ms between speed recalculations (smoothing). */
const MIN_UPDATE_INTERVAL_MS = 500;

/** Tracks upload progress to calculate speed and ETA. */
export class UploadProgressTracker {
  constructor() {
    this.startTime = null;
    this.lastUpdateTime = null;
    this.lastBytesUploaded = 0;
    this.totalBytes = 0;
    this.uploadSpeed = 0;
    this.speedSamples = [];
    this.maxSamples = 10;
  }

  start(bytesUploaded = 0) {
    this.startTime = Date.now();
    this.lastUpdateTime = this.startTime;
    this.lastBytesUploaded = bytesUploaded;
    this.uploadSpeed = 0;
    this.speedSamples = [];
  }

  update(bytesUploaded, bytesTotal) {
    if (!this.startTime) {
      this.start(bytesUploaded);
      this.totalBytes = bytesTotal;
      return;
    }
    this.totalBytes = bytesTotal;
    const now = Date.now();
    const timeDiff = now - this.lastUpdateTime;

    if (timeDiff < MIN_UPDATE_INTERVAL_MS) return;

    const bytesDiff = bytesUploaded - this.lastBytesUploaded;
    const currentSpeed = (bytesDiff / timeDiff) * 1000;
    this.speedSamples.push(currentSpeed);
    if (this.speedSamples.length > this.maxSamples) this.speedSamples.shift();
    this.uploadSpeed = this.speedSamples.reduce((a, b) => a + b, 0) / this.speedSamples.length;
    this.lastUpdateTime = now;
    this.lastBytesUploaded = bytesUploaded;
  }

  getSpeed() {
    return this.uploadSpeed;
  }

  getFormattedSpeed() {
    return this.formatSpeed(this.uploadSpeed);
  }

  getETA(bytesUploaded, bytesTotal) {
    if (this.uploadSpeed === 0 || bytesUploaded === 0) return null;
    const remainingBytes = bytesTotal - bytesUploaded;
    return remainingBytes / this.uploadSpeed;
  }

  getFormattedETA(bytesUploaded, bytesTotal) {
    const eta = this.getETA(bytesUploaded, bytesTotal);
    if (!eta || !isFinite(eta)) return "calculating...";
    return this.formatTime(eta);
  }

  formatSpeed(bytesPerSecond) {
    if (bytesPerSecond === 0 || !isFinite(bytesPerSecond) || isNaN(bytesPerSecond)) return "0 B/s";
    const units = ["B/s", "KB/s", "MB/s", "GB/s"];
    const k = 1024;
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return (bytesPerSecond / Math.pow(k, i)).toFixed(1) + " " + units[i];
  }

  formatTime(seconds) {
    if (seconds < 0 || !seconds || isNaN(seconds)) return "calculating...";
    if (seconds < 60) return Math.ceil(seconds) + "s";
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.ceil(seconds % 60);
      return `${minutes}m ${secs}s`;
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.ceil((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }

  reset() {
    this.startTime = null;
    this.lastUpdateTime = null;
    this.lastBytesUploaded = 0;
    this.totalBytes = 0;
    this.uploadSpeed = 0;
    this.speedSamples = [];
  }
}
